import React, { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Bell, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { update_alert } from "../../watchlist/api";
import type { Alert } from "../../watchlist/types";
import { SCHEDULE_PRESETS } from "../../watchlist/types";
import DaysSelect from "../watchlist/DaysSelect";

export default function ConversationAlert({ alert }: { alert: Alert }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const update = async (patch: Parameters<typeof update_alert>[1]) => {
    setBusy(true);
    setError(false);
    try {
      await update_alert(alert.id, patch);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  const days = alert.schedule?.everyDays ?? 7;
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="mt-3 inline-flex items-center gap-1.5 self-start rounded-full border border-outline px-2.5 py-1.5 text-xs text-primary"
        >
          <Bell size={13} />
          {!alert.enabled
            ? t("chat_alert_paused")
            : days === 7
              ? t("watchlist.runs_weekly")
              : t("watchlist.runs_every_n_days_count", { count: days })}
          <ChevronDown size={12} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          className="z-50 w-64 space-y-3 rounded-xl border border-outline bg-surface p-3 text-sm shadow-lg"
        >
          <p className="font-medium">{alert.title}</p>
          <fieldset disabled={busy} className="space-y-3">
            <DaysSelect
              value={days}
              presets={SCHEDULE_PRESETS}
              onChange={everyDays => void update({ schedule: { everyDays } })}
              format={count => t("watchlist.runs_every_n_days_count", { count })}
              unitLabel={t("watchlist.days_between_runs")}
            />
            <button
              type="button"
              className="block text-primary"
              onClick={() => void update({ enabled: !alert.enabled })}
            >
              {t(alert.enabled ? "chat_pause_alert" : "chat_resume_alert")}
            </button>
          </fieldset>
          <button
            type="button"
            className="block text-primary"
            onClick={() => navigate(`/watchlist/${alert.id}/`, { state: { edit: true } })}
          >
            {t("chat_manage_alert")}
          </button>
          {error && (
            <p role="alert" className="text-danger-strong">
              {t("chat_alert_update_failed")}
            </p>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
